import React, { useEffect, useState, useMemo } from "react";
import { Button, Checkbox, FormControlLabel, Grid, TextField, Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import AppForm from "components/Shared/AppForm";
import BaseSelect from "components/Shared/Base/BaseSelect";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseModal from "components/Shared/BaseModel/BaseModel";
import AccountListModalForm from "./AccountListModalForm";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { useAddListMutation } from "store/services/listService";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";
import { formHookInputHelper } from "utils/formHookField";
import { useAuth } from "hooks";
import { useLazyGetVendorShippingRateQuery } from "store/services/vendorProfileApi";
import { useDetailEntityQuery } from "store/services/entityApi";
import DocumentationFeeItemCard from "./DocumentationFeeItemCard";

const PAYER_OPTIONS = ["Sender", "Receiver", "Vendor"];
const STATUS_OPTIONS = {
  Charge: ["Paid", "Not Paid"],
  CashOnDelivery: ["Paid", "Not Paid"],
  Discount: ["Applied", "Not Applied"],
};
const CHARGE_TYPE_OPTIONS = ["Charge", "Discount", "Cash on Delivery"];
const PAYMENT_LOCATION_OPTIONS = ["Source", "Destination"];

// Shared compact field sizing for both the main form and the added line-item cards.
// xs=12 stacks on very narrow screens, sm=6 pairs two fields per row inside the drawer.
const FIELD_GRID_PROPS = { item: true, xs: 12, sm: 6 };
const FIELD_SIZE = "small";

const ShipmentPriceForm = ({
  isBusy = false,
  price = null,
  shipmentId,
  onSubmit = () => { },
  onCancel = () => { },
  feeTypeList,
  discountTypeList,
}) => {

  const { viewEntity } = useAuth();

  // Fetch fresh entity data to get current documentationFee (viewEntity in auth state is stale — set at login)
  const entityId = viewEntity?.entity?._id;
  const { data: freshEntityData } = useDetailEntityQuery(entityId, { skip: !entityId });

  // Use fresh entity data for documentationFee, fall back to auth state
  const entityDocumentationFee = freshEntityData?.documentationFee ?? viewEntity?.entity?.documentationFee ?? null;

  const { ShipmentDetail } = useShipmentDetailContext();

  const { isAccountAdmin, isSystemAdmin, loggedInUser } = useAuth();
  const [lineItems, setLineItems] = useState([]);
  const [showCurrentForm, setShowCurrentForm] = useState(true);

  const [
    getDDLUserList,
    { data: userList = [], isLoading: isUserListLoading },
  ] = useLazyGetDDLSystemUserListQuery();

  // Fix vendor shipping rate query
  const [
    getVendorShippingRate,
    { data: vendorShippingRateData, isLoading: isLoadingShippingRate },
  ] = useLazyGetVendorShippingRateQuery();

  const transformedUserList = useMemo(() => {
    return userList.map((user) => ({
      value: user._id,
      label: user.name || user.email || "Unnamed User",
    }));
  }, [userList]);

  const isVendorSenders = useMemo(() => {
    return ShipmentDetail?.vendor && !ShipmentDetail?.customerId;
  }, [ShipmentDetail?.vendor, ShipmentDetail?.customerId]);

  const INITIAL_STATE = useMemo(() => ({
    feeItem: "",
    charge: "",
    chargeType: "Charge",
    payer: isVendorSenders ? "Vendor" : "Sender",
    paymentLocation: "Source",
    paymentStatus: "Not Paid",
    discountStatus: "Not Applied",
    responsibleUser: loggedInUser?._id || null,
    deductFromCOD: false,
  }), [isVendorSenders, loggedInUser?._id]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    setError,
    getValues,
    clearErrors,
    trigger,
  } = useForm({
    defaultValues: INITIAL_STATE,
    mode: "onSubmit",
  });

  const chargeType = watch("chargeType");
  const payer = watch("payer");
  const [isAccountListModalActive, setIsAccountListModalActive] = useState(false);
  const feeItemValue = watch("feeItem");
  const chargeValue = watch("charge");

  useEffect(() => {
    setLineItems([]);
    setShowCurrentForm(true);
  }, [price]);

  const [addEntityListOption, { isLoading: addingAccountListModalOption }] =
    useAddListMutation();

  const autocompleteOptions = useMemo(() => {
    const options = chargeType === "Discount"
      ? discountTypeList
      : feeTypeList.filter(
          (item) => item.label !== "Documentation & Processing Fee",
        );
    return options.filter((item) => item.value && item.label);
  }, [chargeType, feeTypeList, discountTypeList]);

  const hasVendorAndCOD = useMemo(() => {
    return ShipmentDetail?.vendor;
  }, [ShipmentDetail]);


  const activeShippingRate = useMemo(() => {
    if (!vendorShippingRateData?.data) return null;
    const activeRate = vendorShippingRateData.data.find(
      (rate) => rate.isActive === true
    );
    return activeRate?.shippingRate || null;
  }, [vendorShippingRateData]);

  // Check if selected fee item is BASE - Standard charge
  const isBaseStandardCharge = useMemo(() => {
    if (!feeItemValue || !feeTypeList?.length) return false;

    const selectedItem = feeTypeList.find(item => item.value === feeItemValue);
    // Check both name and label to be safe
    return selectedItem?.name === "BASE - Standard charge" ||
      selectedItem?.label === "BASE - Standard charge";
  }, [feeItemValue, feeTypeList]);

  // Check if selected fee item is Documentation & Processing Fee
  // NOTE: feeTypeList is pre-mapped to { label, value } — there is no 'name' property
  const isDocumentationFee = useMemo(() => {
    if (!feeItemValue || !feeTypeList?.length) return false;

    const selectedItem = feeTypeList.find(item => item.value === feeItemValue);
    return selectedItem?.label === "Documentation & Processing Fee";
  }, [feeItemValue, feeTypeList]);

  const isDocumentationFeeChargeLocked =
    chargeType === "Charge" &&
    isDocumentationFee &&
    (Boolean(price) ||
      (entityDocumentationFee?.isActive &&
        entityDocumentationFee?.documentationFee !== undefined &&
        entityDocumentationFee?.documentationFee !== null));

  const isVendorSender = useMemo(() => {
    return ShipmentDetail?.vendor && !ShipmentDetail?.customerId;
  }, [ShipmentDetail?.vendor, ShipmentDetail?.customerId]);

  const payerOptions = useMemo(() => {
    let options = [...PAYER_OPTIONS];

    if (!ShipmentDetail?.vendor) {
      options = options.filter(option => option !== "Vendor");
    }

    if (isVendorSender) {
      options = options.filter(option => option !== "Sender");
    }

    return options;
  }, [ShipmentDetail?.vendor, isVendorSender]);

  // Fetch vendor shipping rate when component mounts
  useEffect(() => {
    if (ShipmentDetail?.vendor?._id) {
      // Pass vendorId as string, not object
      getVendorShippingRate({
        vendorId: ShipmentDetail.vendor._id
      });
    }
  }, [ShipmentDetail?.vendor, getVendorShippingRate]);
  // Calculate and set charge automatically for BASE - Standard charge items
  useEffect(() => {
    if (price || chargeType !== "Charge") return;

    if (!ShipmentDetail?.vendor || !activeShippingRate) return;

    if (!ShipmentDetail?.weight) return;

    if (!isBaseStandardCharge) return;

    // Calculate: rate * weight
    const calculatedCharge = (activeShippingRate * ShipmentDetail.weight).toFixed(2);

    if (!chargeValue || chargeValue === "0" || chargeValue === "0.00") {
      setValue("charge", calculatedCharge, { shouldValidate: true });
    }
  }, [
    price,
    chargeType,
    ShipmentDetail?.vendor,
    ShipmentDetail?.weight,
    activeShippingRate,
    isBaseStandardCharge,
    chargeValue,
    setValue,
  ]);

  // Reset charge when fee item changes
  useEffect(() => {
    if (price || chargeType !== "Charge") return;

    if (feeItemValue && !isBaseStandardCharge) {
      // Clear if the auto-calculated BASE amount was previously filled
      if (ShipmentDetail?.vendor && activeShippingRate && ShipmentDetail?.weight) {
        const calculatedCharge = (activeShippingRate * ShipmentDetail.weight).toFixed(2);
        if (getValues("charge") === calculatedCharge) {
          setValue("charge", "", { shouldValidate: true });
        }
      }
    }

    if (feeItemValue && !isDocumentationFee) {
      // Clear if the auto-filled doc fee amount was previously set
      const entityDocFeeAmount = entityDocumentationFee?.documentationFee;
      if (entityDocFeeAmount !== undefined && String(getValues("charge")) === String(entityDocFeeAmount)) {
        setValue("charge", "", { shouldValidate: true });
      }
    }
  }, [feeItemValue, isBaseStandardCharge, isDocumentationFee, price, chargeType, setValue, getValues, ShipmentDetail, activeShippingRate, entityDocumentationFee]);

  // Auto-populate charge for Documentation & Processing Fee
  useEffect(() => {
    if (price || chargeType !== "Charge") return;
    if (!isDocumentationFee) return;

    const entityDocFee = entityDocumentationFee;
    if (!entityDocFee?.isActive) return;

    const feeAmount = entityDocFee?.documentationFee;
    if (feeAmount === undefined || feeAmount === null) return;

    // Selecting this fee item always uses the entity's configured amount.
    // chargeValue is intentionally not a dependency, so editing the amount
    // manually afterwards does not immediately overwrite the user's value.
    setValue("charge", String(feeAmount), { shouldValidate: true });
  }, [
    price,
    chargeType,
    isDocumentationFee,
    entityDocumentationFee,
    setValue,
  ]);

  useEffect(() => {
    getDDLUserList({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
  }, [getDDLUserList, viewEntity?.entity?._id, viewEntity?.entity?.account?._id]);

  useEffect(() => {
    if (chargeType === "Cash on Delivery") return;

    if (payer === "Vendor") {
      setValue("paymentLocation", "Source", { shouldValidate: true });

      if (ShipmentDetail?.vendor) {
        setValue("responsibleUser", ShipmentDetail.vendor._id, { shouldValidate: true });
      } else {
        setValue("responsibleUser", loggedInUser?._id || null, { shouldValidate: true });
      }
    } else if (payer === "Sender") {
      setValue("paymentLocation", "Source", { shouldValidate: true });
      if (!price) {
        setValue("responsibleUser", loggedInUser?._id || null, { shouldValidate: true });
      }
    } else if (payer === "Receiver") {
      setValue("paymentLocation", "Destination", { shouldValidate: true });
      setValue("responsibleUser", null, { shouldValidate: true });
    }
  }, [payer, price, chargeType, setValue, loggedInUser, ShipmentDetail]);

  useEffect(() => {
    const currentFeeItem = getValues("feeItem");
    if (
      currentFeeItem &&
      !autocompleteOptions.some((opt) => opt.value === currentFeeItem)
    ) {
      setValue("feeItem", "", { shouldValidate: false });
      clearErrors("feeItem");
    }
  }, [chargeType, autocompleteOptions, setValue, clearErrors, getValues]);

  useEffect(() => {
    if (price) return;
    if (chargeType === "Cash on Delivery") {
      if (getValues("payer") !== "Receiver") {
        setValue("payer", "Receiver", { shouldValidate: true });
      }
      if (getValues("paymentLocation") !== "Destination") {
        setValue("paymentLocation", "Destination", { shouldValidate: true });
      }
      setValue("deductFromCOD", false, { shouldValidate: true });
      setValue("responsibleUser", null, { shouldValidate: true });
    } else if (chargeType === "Charge") {
      setValue("payer", isVendorSender ? "Vendor" : "Sender", { shouldValidate: true });
      setValue("paymentLocation", "Source", { shouldValidate: true });

      if (ShipmentDetail?.vendor) {
        setValue("responsibleUser", ShipmentDetail.vendor._id, { shouldValidate: true });
      } else {
        setValue("responsibleUser", loggedInUser?._id || null, { shouldValidate: true });
      }
    }
  }, [chargeType, price, getValues, setValue, loggedInUser, ShipmentDetail, isVendorSender]);

  useEffect(() => {
    if (price) return;
    if (chargeType === "Cash on Delivery") {
      return;
    }

    if (payer === "Vendor") {
      if (ShipmentDetail?.vendor) {
        setValue("responsibleUser", ShipmentDetail.vendor._id, { shouldValidate: true });
      } else {
        setValue("responsibleUser", loggedInUser?._id || null, { shouldValidate: true });
      }
    } else if (payer === "Sender") {
      setValue("responsibleUser", loggedInUser?._id || null, { shouldValidate: true });
    } else if (payer === "Receiver") {
      setValue("responsibleUser", null, { shouldValidate: true });
    }
  }, [payer, price, chargeType, setValue, loggedInUser, ShipmentDetail]);

  useEffect(() => {
    if (!price) {
      return;
    }

    const validFeeItems =
      chargeType === "Discount"
        ? discountTypeList
        : feeTypeList.filter(
            (item) => item.label !== "Documentation & Processing Fee",
          );
    const feeItemId = price.feeItem?._id || price.feeItem;
    const isValidFeeItem =
      feeItemId && validFeeItems.some((item) => item.value === feeItemId);

    const formValues = {
      feeItem: isValidFeeItem ? feeItemId : "",
      charge: price.charge ? Math.abs(parseFloat(price.charge)).toFixed(2) : "",
      chargeType: price.chargeType || "Charge",
      payer: price.payer || "Vendor",
      paymentLocation:
        price.paymentLocation ||
        (price.payer === "Receiver" || price.chargeType === "Cash on Delivery"
          ? "Destination"
          : "Source"),
      paymentStatus:
        price.chargeType === "Discount"
          ? "Not Paid"
          : price.paymentStatus || "Not Paid",
      discountStatus:
        price.chargeType === "Discount"
          ? price.discountStatus || "Not Applied"
          : "Not Applied",
      responsibleUser:
        price.responsibleUser?._id || price.responsibleUser || loggedInUser?._id || null,
      deductFromCOD: price.deductFromCOD || false,
    };

    reset(formValues, { keepValues: false });

    if (feeItemId && !isValidFeeItem) {
      setError("feeItem", {
        type: "manual",
        message: `Selected ${price.chargeType === "Charge" ? "fee" : "discount"
          } item is not available. Please select a valid item.`,
      });
    } else {
      clearErrors("feeItem");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, discountTypeList, feeTypeList, reset, setError, clearErrors, loggedInUser]);

  const handleAccountListModal = async (data) => {
    try {
      const newAccountListItem = await addEntityListOption({
        ...data,
        type: chargeType === "Discount" ? "Shipment Discount" : "Shipment Fees",
      }).unwrap();
      toast.success(
        `${chargeType === "Discount" ? "Discount" : "Fee"} Item Created Successfully!`
      );
      setValue("feeItem", newAccountListItem._id, { shouldValidate: true });
      setIsAccountListModalActive(false);
    } catch (error) {
      toast.error(
        `Could Not Create ${chargeType === "Discount" ? "Discount" : "Fee"} Item!`
      );
    }
  };

  const handleSubmitShipmentPrice = async (data) => {
    if (
      data.chargeType !== "Cash on Delivery" &&
      !autocompleteOptions.some((opt) => opt.value === data.feeItem)
    ) {
      setError("feeItem", {
        type: "manual",
        message: "Invalid fee item selected",
      });
      return;
    }

    let responsibleUser;
    if (data.chargeType === "Cash on Delivery") {
      responsibleUser = price
        ? (price.responsibleUser?._id || price.responsibleUser || null)
        : null;
    } else {
      responsibleUser = data.responsibleUser || null;
    }

    const payload = {
      priceId: price?._id,
      shipmentId: shipmentId || ShipmentDetail?._id,
      charge: parseFloat(data.charge),
      chargeType: data.chargeType,
      payer: data.payer,
      paymentLocation:
        data.chargeType === "Cash on Delivery" || data.payer === "Receiver"
          ? "Destination"
          : data.paymentLocation || "Source",
      feeItem: data.chargeType === "Cash on Delivery" ? null : data.feeItem,
      ...(data.chargeType !== "Discount" && {
        paymentStatus: data.paymentStatus || "Not Paid",
      }),
      ...(data.chargeType === "Discount" && {
        discountStatus: data.discountStatus,
      }),
      responsibleUser: responsibleUser,
      responsibleUserType:
        data.payer === "Vendor" && ShipmentDetail?.vendor
          ? "VendorProfile"
          : "User",
      deductFromCOD: data.deductFromCOD,
    };

    const cleanedLineItems = lineItems.map((item) => ({
      ...item,
      feeItem: item.chargeType === "Cash on Delivery" || !item.feeItem ? undefined : item.feeItem,
      responsibleUser: item.chargeType === "Cash on Delivery" ? null : (item.responsibleUser || null),
    }));

    try {
      await onSubmit(price ? payload : { items: [...cleanedLineItems, payload] });
    } catch (error) {
      toast.error("Failed to save shipment price. Please try again.");
      console.error("Submission error:", error);
    }
  };

  const handleAddAnother = async () => {
    const valid = await trigger();
    if (!valid) return;

    const data = getValues();
    if (
      data.chargeType !== "Cash on Delivery" &&
      !autocompleteOptions.some((opt) => opt.value === data.feeItem)
    ) {
      setError("feeItem", { type: "manual", message: "Invalid fee item selected" });
      return;
    }

    setLineItems((items) => [
      ...items,
      {
        ...data,
        feeItem: data.chargeType === "Cash on Delivery" || !data.feeItem ? undefined : data.feeItem,
        charge: parseFloat(data.charge),
        paymentLocation:
          data.chargeType === "Cash on Delivery" || data.payer === "Receiver"
            ? "Destination"
            : data.paymentLocation || "Source",
        shipmentId: shipmentId || ShipmentDetail?._id,
      },
    ]);
    reset(INITIAL_STATE);
  };

  const getPaymentLocationOptions = () => {
    return chargeType === "Cash on Delivery" || payer === "Receiver"
      ? ["Destination"]
      : PAYMENT_LOCATION_OPTIONS;
  };

  const getPaymentLocationLabel = (value) => {
    const sourceName = ShipmentDetail?.sourceBranch?.name || "Source";
    const destinationName =
      ShipmentDetail?.destinationBranch?.name || "Destination";
    return value === "Source"
      ? `Source: ${sourceName}`
      : `Destination: ${destinationName}`;
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index] = { ...newItems[index], [field]: value };

    if (field === "chargeType") {
      if (value === "Cash on Delivery") {
        newItems[index].feeItem = undefined;
        newItems[index].payer = "Receiver";
        newItems[index].paymentLocation = "Destination";
        newItems[index].deductFromCOD = false;
        newItems[index].responsibleUser = null;
      } else if (value === "Charge") {
        newItems[index].payer = isVendorSender ? "Vendor" : "Sender";
        newItems[index].paymentLocation = "Source";
        newItems[index].responsibleUser = ShipmentDetail?.vendor ? ShipmentDetail.vendor._id : (loggedInUser?._id || null);
      }
    } else if (field === "payer" && newItems[index].chargeType !== "Cash on Delivery") {
      if (value === "Vendor") {
        newItems[index].paymentLocation = "Source";
        newItems[index].responsibleUser = ShipmentDetail?.vendor ? ShipmentDetail.vendor._id : (loggedInUser?._id || null);
      } else if (value === "Sender") {
        newItems[index].paymentLocation = "Source";
        newItems[index].responsibleUser = loggedInUser?._id || null;
      } else if (value === "Receiver") {
        newItems[index].responsibleUser = null;
      }
    }
    setLineItems(newItems);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems((items) => items.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleRemoveCurrentForm = () => {
    reset(INITIAL_STATE);
    clearErrors();
    setShowCurrentForm(false);
  };

  const handleShowCurrentForm = () => {
    reset(INITIAL_STATE);
    clearErrors();
    setShowCurrentForm(true);
  };

  const handleSubmitSavedLineItems = async () => {
    const cleanedLineItems = lineItems.map((item) => ({
      ...item,
      feeItem: item.chargeType === "Cash on Delivery" || !item.feeItem ? undefined : item.feeItem,
      responsibleUser: item.chargeType === "Cash on Delivery" ? null : (item.responsibleUser || null),
    }));

    try {
      await onSubmit({ items: cleanedLineItems });
    } catch (error) {
      toast.error("Failed to save shipment price. Please try again.");
      console.error("Submission error:", error);
    }
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        {!price && (
          <DocumentationFeeItemCard entityDocumentationFee={entityDocumentationFee} />
        )}
        {!price && lineItems.length > 0 && (
          <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1.5, maxHeight: 360, overflowY: "auto", pr: 1 }}>
            {lineItems.map((item, index) => (
              <Box
                key={`${item.feeItem || item.chargeType}-${index}`}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  position: "relative"
                }}
              >
                <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 1 }}>
                  <Button
                    type="button"
                    size="small"
                    variant="text"
                    color="error"
                    onClick={() => handleRemoveLineItem(index)}
                    disabled={isBusy}
                  >
                    Remove
                  </Button>
                </Box>
                <Typography variant="caption" sx={{ mb: 1, display: "block", color: "text.secondary", fontWeight: 600 }}>
                  Added Form {index + 1}
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid {...FIELD_GRID_PROPS}>
                    <BaseSelect
                      fullWidth
                      size={FIELD_SIZE}
                      label="Price Type"
                      options={ShipmentDetail?.customerId ? ["Charge", "Discount"] : CHARGE_TYPE_OPTIONS}
                      value={item.chargeType || ""}
                      onChange={(e) => updateLineItem(index, "chargeType", e.target.value)}
                      disabled
                    />
                  </Grid>
                  {item.chargeType !== "Cash on Delivery" && (
                    <Grid {...FIELD_GRID_PROPS}>
                      <BaseAutocomplete
                        fullWidth
                        size={FIELD_SIZE}
                        label={item.chargeType === "Discount" ? "Discount Item" : "Fee Item"}
                        options={item.chargeType === "Discount"
                          ? discountTypeList
                          : feeTypeList.filter((opt) => opt.label !== "Documentation & Processing Fee")}
                        value={(item.chargeType === "Discount"
                          ? discountTypeList
                          : feeTypeList.filter((opt) => opt.label !== "Documentation & Processing Fee")
                        ).find((opt) => opt.value === item.feeItem) || null}
                        onChange={(_, data) => updateLineItem(index, "feeItem", data?.value || "")}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(option, value) => option.value === value}
                        disableClearable
                        disabled
                      />
                    </Grid>
                  )}
                  <Grid {...FIELD_GRID_PROPS}>
                    <TextField
                      fullWidth
                      size={FIELD_SIZE}
                      type="number"
                      label={item.chargeType === "Discount" ? "Discount Amount (Rs)" : "Charge Amount (Rs)"}
                      value={item.charge === undefined ? "" : item.charge}
                      onChange={(e) => updateLineItem(index, "charge", e.target.value)}
                      disabled
                    />
                  </Grid>
                  {item.chargeType !== "Discount" && (
                    <>
                      <Grid {...FIELD_GRID_PROPS}>
                        <BaseSelect
                          fullWidth
                          size={FIELD_SIZE}
                          label="Payer"
                          options={PAYER_OPTIONS}
                          value={item.payer || ""}
                          onChange={(e) => updateLineItem(index, "payer", e.target.value)}
                          disabled
                        />
                      </Grid>
                      <Grid {...FIELD_GRID_PROPS}>
                        <BaseSelect
                          fullWidth
                          size={FIELD_SIZE}
                          label="Payment Location"
                          options={item.chargeType === "Cash on Delivery" || item.payer === "Receiver" ? ["Destination"] : PAYMENT_LOCATION_OPTIONS}
                          value={item.paymentLocation || ""}
                          onChange={(e) => updateLineItem(index, "paymentLocation", e.target.value)}
                          disabled
                        />
                      </Grid>
                      <Grid {...FIELD_GRID_PROPS}>
                        <BaseSelect
                          fullWidth
                          size={FIELD_SIZE}
                          label="Payment Status"
                          options={STATUS_OPTIONS[item.chargeType === "Cash on Delivery" ? "CashOnDelivery" : "Charge"]}
                          value={item.paymentStatus || ""}
                          onChange={(e) => updateLineItem(index, "paymentStatus", e.target.value)}
                          disabled
                        />
                      </Grid>
                    </>
                  )}
                  {item.chargeType === "Discount" && (
                    <Grid {...FIELD_GRID_PROPS}>
                      <BaseSelect
                        fullWidth
                        size={FIELD_SIZE}
                        label="Discount Status"
                        options={STATUS_OPTIONS.Discount}
                        value={item.discountStatus || ""}
                        onChange={(e) => updateLineItem(index, "discountStatus", e.target.value)}
                        disabled
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </Box>
        )}
        {!price && !showCurrentForm && (
          <Button
            type="button"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleShowCurrentForm}
            disabled={isBusy}
            sx={{ mb: 2 }}
          >
            Add Price Form
          </Button>
        )}
        {(!price || showCurrentForm) && (
        <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          {!price && (
            <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                {lineItems.length > 0 ? `New Form ${lineItems.length + 1}` : "New Form"}
              </Typography>
              <Button
                type="button"
                size="small"
                color="error"
                onClick={handleRemoveCurrentForm}
                disabled={isBusy}
              >
                Remove
              </Button>
            </Box>
          )}
          <Grid container spacing={1.5}>
            <Grid {...FIELD_GRID_PROPS}>
              <Controller
                name="chargeType"
                control={control}
                rules={{ required: "Price Type is required" }}
                render={(props) => (
                  <BaseSelect
                    {...formHookInputHelper(props)}
                    required
                    fullWidth
                    size={FIELD_SIZE}
                    label="Price Type"
                    options={
                      ShipmentDetail?.customerId
                        ? ["Charge", "Discount"]
                        : CHARGE_TYPE_OPTIONS
                    }
                    value={props.field.value}
                    onChange={(e) => {
                      const newChargeType = e.target.value;
                      props.field.onChange(newChargeType);

                      if (!price) {
                        setValue("charge", "");
                        setValue("feeItem", "");
                      } else {
                        if (newChargeType === "Cash on Delivery") {
                          setValue("feeItem", "");
                          setValue("payer", "Receiver");
                          setValue("paymentLocation", "Destination");
                        } else if (newChargeType === "Charge") {
                          setValue("payer", isVendorSender ? "Vendor" : "Sender");
                          setValue("paymentLocation", "Source");
                        }
                      }
                    }}
                    disabled={isBusy}
                  />
                )}
              />
            </Grid>
            <Grid {...FIELD_GRID_PROPS}>
              <Controller
                name="feeItem"
                control={control}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    size={FIELD_SIZE}
                    label={
                      chargeType === "Discount" ? "Discount Item" : "Fee Item"
                    }
                    options={autocompleteOptions}
                    value={
                      autocompleteOptions.find(
                        (opt) => opt.value === field.value
                      ) || null
                    }
                    onChange={(_, data) => field.onChange(data?.value || "")}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value
                    }
                    disableClearable
                    freeSolo={false}
                    error={!!errors?.feeItem}
                    helperText={
                      errors?.feeItem?.message ||
                      (autocompleteOptions.length === 0
                        ? `No ${chargeType === "Discount" ? "discount" : "fee"
                        } items available.`
                        : "")
                    }
                    disabled={isBusy || chargeType === "Cash on Delivery"}
                    noOptionsText={`No ${chargeType === "Discount" ? "discount" : "fee"
                      } items available`}
                    {...((isAccountAdmin || isSystemAdmin) && {
                      PaperComponent: (props) => (
                        <BaseAutocomplete.PaperAddNew
                          {...props}
                          buttonLabel={`Add ${chargeType === "Discount" ? "Discount" : "Fee"
                            } Item`}
                          onAddNew={() => setIsAccountListModalActive(true)}
                        />
                      ),
                    })}
                  />
                )}
              />
            </Grid>
            <Grid {...FIELD_GRID_PROPS}>
              <Controller
                name="charge"
                control={control}
                rules={{
                  required: "Amount is required",
                  validate: (value) => {
                    const numValue = parseFloat(value);
                    if (isNaN(numValue)) return "Amount must be a valid number";
                    if (
                      chargeType !== "Discount" &&
                      (isAccountAdmin ? numValue < 0 : numValue <= 0)
                    )
                      return isAccountAdmin
                        ? "Amount cannot be negative"
                        : "Amount must be greater than 0";
                    if (chargeType === "Discount" && numValue < 0)
                      return "Discount amount must be non-negative";
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    size={FIELD_SIZE}
                    label={
                      chargeType === "Discount"
                        ? "Discount Amount (Rs)"
                        : "Charge Amount (Rs)"
                    }
                    type="number"
                    inputProps={{
                      step: "0.01",
                      min: chargeType === "Discount" ? "0" : "0.01",
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "e" || e.key === "E") {
                        e.preventDefault();
                      }
                    }}
                    error={!!errors?.charge}
                    helperText={
                      errors?.charge?.message ||
                      (isDocumentationFee &&
                        !price &&
                        chargeType === "Charge" &&
                        entityDocumentationFee?.isActive
                        ? `Auto-filled: Documentation & Processing Fee = ${entityDocumentationFee.documentationFee}`
                        : ShipmentDetail?.vendor &&
                          activeShippingRate &&
                          chargeType === "Charge" &&
                          !price &&
                          isBaseStandardCharge
                          ? `Auto-calculated: ${activeShippingRate} (rate) × ${ShipmentDetail?.weight} (weight) = ${field.value}`
                          : ShipmentDetail?.vendor &&
                            chargeType === "Charge" &&
                            !price &&
                            isBaseStandardCharge &&
                            isLoadingShippingRate
                            ? "Loading shipping rate..."
                            : "")
                    }
                    disabled={isBusy || isDocumentationFeeChargeLocked}
                  />
                )}
              />
            </Grid>
            {!price && hasVendorAndCOD && chargeType === "Charge" && (
              <Grid {...FIELD_GRID_PROPS} sx={{ display: "flex", alignItems: "center" }}>
                <Controller
                  name="deductFromCOD"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          disabled={isBusy}
                          size={FIELD_SIZE}
                        />
                      }
                      label="Deduct from COD"
                    />
                  )}
                />
              </Grid>
            )}
            {chargeType !== "Discount" && (
              <>
                <Grid {...FIELD_GRID_PROPS}>
                  <Controller
                    name="payer"
                    control={control}
                    rules={{ required: "Payer is required" }}
                    render={({ field }) => (
                      <BaseSelect
                        {...field}
                        required
                        fullWidth
                        size={FIELD_SIZE}
                        label="Payer"
                        options={payerOptions}
                        value={
                          chargeType === "Cash on Delivery"
                            ? "Receiver"
                            : field.value
                        }
                        onChange={(e) => field.onChange(e.target.value)}
                        error={!!errors?.payer}
                        helperText={errors?.payer?.message}
                        disabled={
                          isBusy ||
                          chargeType === "Cash on Delivery" ||
                          Boolean(price)
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid {...FIELD_GRID_PROPS}>
                  <Controller
                    name="paymentLocation"
                    control={control}
                    render={({ field }) => (
                      <BaseSelect
                        {...field}
                        fullWidth
                        required
                        size={FIELD_SIZE}
                        label="Payment Location"
                        options={getPaymentLocationOptions()}
                        getOptionLabel={getPaymentLocationLabel}
                        value={
                          chargeType === "Cash on Delivery" || payer === "Receiver"
                            ? "Destination"
                            : field.value || "Source"
                        }
                        onChange={(e) => field.onChange(e.target.value)}
                        error={!!errors?.paymentLocation}
                        helperText={errors?.paymentLocation?.message}
                        disabled={
                          isBusy ||
                          chargeType === "Cash on Delivery" ||
                          payer === "Receiver" ||
                          payer === "Vendor" ||
                          payer === "Sender"
                        }
                      />
                    )}
                  />
                </Grid>
                {!(chargeType === "Cash on Delivery" || payer === "Receiver") && (
                  <Grid {...FIELD_GRID_PROPS}>
                    <Controller
                      name="responsibleUser"
                      control={control}
                      rules={{ required: "Responsible User is required" }}
                      render={({ field }) => (
                        <BaseAutocomplete
                          {...field}
                          fullWidth
                          size={FIELD_SIZE}
                          label="Responsible User"
                          options={transformedUserList}
                          value={
                            payer === "Vendor" && ShipmentDetail?.vendor
                              ? { value: ShipmentDetail.vendor._id, label: ShipmentDetail.vendor.shopName }
                              : transformedUserList.find((opt) => opt.value === field.value) || null
                          }
                          onChange={(_, data) =>
                            field.onChange(data?.value || loggedInUser?._id || null)
                          }
                          getOptionLabel={(option) => option.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.value === value
                          }
                          disableClearable
                          freeSolo={false}
                          error={!!errors?.responsibleUser}
                          helperText={
                            errors?.responsibleUser?.message ||
                            (transformedUserList.length === 0 ? "No users available." : "")
                          }
                          disabled={isBusy || isUserListLoading || Boolean(payer === "Vendor" && ShipmentDetail?.vendor)}
                          noOptionsText="No users available"
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid {...FIELD_GRID_PROPS}>
                  <Controller
                    name="paymentStatus"
                    control={control}
                    rules={{ required: "Payment Status is required" }}
                    render={({ field }) => (
                      <BaseSelect
                        {...field}
                        required
                        fullWidth
                        size={FIELD_SIZE}
                        label="Payment Status"
                        options={
                          STATUS_OPTIONS[
                          chargeType === "Cash on Delivery"
                            ? "CashOnDelivery"
                            : "Charge"
                          ]
                        }
                        value={field.value || "Not Paid"}
                        onChange={(e) => field.onChange(e.target.value)}
                        error={!!errors?.paymentStatus}
                        helperText={errors?.paymentStatus?.message}
                        disabled
                      />
                    )}
                  />
                </Grid>
              </>
            )}
            {chargeType === "Discount" && (
              <Grid {...FIELD_GRID_PROPS}>
                <Controller
                  name="discountStatus"
                  control={control}
                  rules={{ required: "Discount Status is required" }}
                  render={({ field }) => (
                    <BaseSelect
                      {...field}
                      required
                      fullWidth
                      size={FIELD_SIZE}
                      label="Discount Status"
                      options={STATUS_OPTIONS.Discount}
                      value={field.value || "Not Applied"}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!errors?.discountStatus}
                      helperText={errors?.discountStatus?.message}
                      disabled={isBusy}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Box>
        )}
      </AppForm.Content>
      <BaseModal
        isOpen={isAccountListModalActive}
        onClose={() => setIsAccountListModalActive(false)}
        title={`Add ${chargeType === "Discount" ? "Discount" : "Fee"} Item`}
        width="800px"
        top="50%"
        left="58%"
      >
        <AccountListModalForm
          label={`Add ${chargeType === "Discount" ? "Discount" : "Fee"} Item`}
          isBusy={addingAccountListModalOption}
          onSubmit={handleAccountListModal}
          onCancel={() => setIsAccountListModalActive(false)}
          chargeType={chargeType}
        />
      </BaseModal>
      <AppForm.Actions>
        <Button disabled={isBusy} onClick={onCancel}>
          Cancel
        </Button>
        {!price && (
          <Button
            disabled={isBusy}
            onClick={handleSubmit(handleAddAnother)}
            startIcon={<AddIcon />}
          >
            Add Another
          </Button>
        )}
        <Button
          variant="contained"
          onClick={
            !price && !showCurrentForm
              ? handleSubmitSavedLineItems
              : handleSubmit(handleSubmitShipmentPrice)
          }
          disabled={isBusy}
        >
          {price ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default ShipmentPriceForm;
